import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Sun, Moon, Building, User, Lock } from 'lucide-react';

const Login = () => {
  const getInitialFormData = () => {
    try {
      const rememberedRaw = localStorage.getItem('login-remember');
      const remembered = rememberedRaw ? JSON.parse(rememberedRaw) : null;
      return {
        company: remembered?.company || '',
        username: remembered?.username || '',
        password: ''
      };
    } catch (_) {
      return { company: '', username: '', password: '' };
    }
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem('login-remember'));
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [alertState, setAlertState] = useState({
    visible: false,
    title: '',
    message: ''
  });
  
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };
    setFormData(next);
    // Live-persist remembered fields except password
    if (rememberMe && (name === 'company' || name === 'username')) {
      localStorage.setItem('login-remember', JSON.stringify({
        company: name === 'company' ? value : next.company,
        username: name === 'username' ? value : next.username
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertState((prev) => ({ ...prev, visible: false }));
    setIsLoading(true);
    
    try {
      await login({
        username: formData.username,
        password: formData.password
      }, { keepSignedIn });

      // Persist or clear remembered fields after a successful login
      if (rememberMe) {
        localStorage.setItem('login-remember', JSON.stringify({
          company: formData.company,
          username: formData.username
        }));
      } else {
        localStorage.removeItem('login-remember');
      }
    } catch (error) {
      console.error('Login error:', error);
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;
      const validationDetails = error.response?.data?.errors?.map(e => `${e.field}: ${e.message}`).join(', ');
      if (!error.response) {
        setAlertState({
          visible: true,
          title: 'Bağlantı Sorunu',
          message: 'Sunucuya ulaşılamadı. İnternet bağlantınızı veya VPN ayarlarınızı kontrol edin.'
        });
      } else if (status === 400 || status === 401) {
        setAlertState({
          visible: true,
          title: 'Kullanıcı Adı ya da Şifre Hatalı',
          message: serverMessage || 'Lütfen bilgilerinizi kontrol ederek tekrar deneyin.'
        });
      } else {
        setAlertState({
          visible: true,
          title: 'İşlem Tamamlanamadı',
          message: validationDetails || serverMessage || 'Beklenmeyen bir hata oluştu. Birkaç dakika sonra tekrar deneyin.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-lg bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700"
      >
        {isDark ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
      </button>

      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.jpg" 
              alt="Jansoft Logo" 
              className="w-16 h-16 object-contain rounded-full"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center" style={{display: 'none'}}>
              <span className="text-white text-2xl font-bold">J</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Jansoft ERP
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Kumaş Yönetim Sistemi
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Firma
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="pl-10 input-field"
                  placeholder="Firma adı"
                />
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Kullanıcı Adı
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="pl-10 input-field"
                  placeholder="Kullanıcı adı"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Şifre
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10 input-field"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={rememberMe}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRememberMe(checked);
                  if (checked) {
                    localStorage.setItem('login-remember', JSON.stringify({
                      company: formData.company,
                      username: formData.username
                    }));
                  } else {
                    localStorage.removeItem('login-remember');
                  }
                }}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Firma ve kullanıcıyı hatırla
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="keep-signed-in"
                name="keep-signed-in"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
              />
              <label htmlFor="keep-signed-in" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Oturumu açık tut
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Giriş yapılıyor...
                </div>
              ) : (
                'Giriş Yap'
              )}
            </button>
          </div>
        </form>
      </div>
      {alertState.visible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-100 dark:border-gray-700 p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {alertState.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              {alertState.message}
            </p>
            <button
              type="button"
              onClick={() => setAlertState({ visible: false, title: '', message: '' })}
              className="inline-flex w-full justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Tamam
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
